import React, { useCallback, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { Fragment } from "react"
import { TAgetBrandAIHealthAnalysis } from "../../../services/brandAPI"

// Types matching MongoDB schema
interface QuickStats {
  days_since_login?: number
  days_since_last_contact?: number
  balance?: number
  balance_usage_percent?: number
  stuck_applications?: number
  active_campaigns?: number
  completion_rate?: number
}

interface RiskFactor {
  factor: string
  severity: "critical" | "high" | "medium" | "low"
  value?: number
  threshold?: number
  label?: string
}

interface Labels {
  health_score: number
  urgency_score?: number
  churn_probability?: number
  status: "critical" | "warning" | "stable" | "healthy"
  status_color?: string
  customer_status?: string
  activity_status?: string
  budget_status?: string
  campaign_status?: string
  priority_rank: string
  engagement_trend?: string
  churn_risk_level?: string
  action_required?: boolean
  action_type?: string
  tags?: string[]
}

interface NextAction {
  type?: string
  priority?: string
  label?: string
  reason?: string
}

interface AIRecommendation {
  priority?: string
  action?: string
  expected_impact?: string
}

interface AIHealthAnalysisData {
  labels: Labels
  quick_stats?: QuickStats
  risk_factors?: RiskFactor[]
  ai_summary?: string
  ai_key_insight?: string
  ai_recommended_action?: string
  ai_root_causes?: string[]
  ai_recommendations?: AIRecommendation[]
  next_action?: NextAction
  analyzed_at?: string
  expires_at?: string
  model?: string
  processing_time_ms?: number
}

interface Props {
  isOpen: boolean
  onClose: () => void
  brandId: string
  brandName: string
  initialData?: AIHealthAnalysisData | null
}

const statusConfig = {
  critical: {
    bg: "bg-gradient-to-r from-red-500 to-red-600",
    lightBg: "bg-red-50 dark:bg-red-900/20",
    text: "text-red-600 dark:text-red-400",
    border: "border-red-200 dark:border-red-800",
    label: "KRİTİK",
    icon: "🔴",
    emoji: "🚨"
  },
  warning: {
    bg: "bg-gradient-to-r from-amber-500 to-orange-500",
    lightBg: "bg-amber-50 dark:bg-amber-900/20",
    text: "text-amber-600 dark:text-amber-400",
    border: "border-amber-200 dark:border-amber-800",
    label: "DİKKAT",
    icon: "🟠",
    emoji: "⚠️"
  },
  stable: {
    bg: "bg-gradient-to-r from-blue-500 to-indigo-500",
    lightBg: "bg-blue-50 dark:bg-blue-900/20",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-200 dark:border-blue-800",
    label: "STABİL",
    icon: "🔵",
    emoji: "📊"
  },
  healthy: {
    bg: "bg-gradient-to-r from-emerald-500 to-green-500",
    lightBg: "bg-emerald-50 dark:bg-emerald-900/20",
    text: "text-emerald-600 dark:text-emerald-400",
    border: "border-emerald-200 dark:border-emerald-800",
    label: "SAĞLIKLI",
    icon: "🟢",
    emoji: "✅"
  },
}

const priorityConfig: Record<string, { color: string; label: string }> = {
  P0: { color: "bg-red-600 text-white", label: "ACİL - Bugün" },
  P1: { color: "bg-orange-500 text-white", label: "Yüksek - 24 Saat" },
  P2: { color: "bg-amber-500 text-white", label: "Orta - Bu Hafta" },
  P3: { color: "bg-blue-500 text-white", label: "Normal - Bu Ay" },
  P4: { color: "bg-slate-400 text-white", label: "Düşük" },
  P5: { color: "bg-slate-300 text-slate-700", label: "Rutin" },
}

const severityColors = {
  critical: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
  high: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
  medium: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  low: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
}

const actionTypeLabels: Record<string, string> = {
  outreach: "📞 İletişim",
  meeting: "📅 Toplantı",
  follow_up: "↩️ Takip",
  escalate: "⬆️ Eskalasyon",
  support: "🛠️ Destek",
  training: "📚 Eğitim",
  upsell: "💰 Upsell",
  monitor: "👁️ İzleme",
  none: "✓ Aksiyon Yok",
}

const formatNumber = (num?: number) => {
  if (num === undefined || num === null) {
    return "-"
  }
  return new Intl.NumberFormat("tr-TR").format(num)
}

const formatCurrency = (amount?: number) => {
  if (amount === undefined || amount === null) {
    return "₺0"
  }
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
  }).format(amount)
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) {
    return "-"
  }
  return new Date(dateStr).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const AIHealthAnalysisModal: React.FC<Props> = ({ isOpen, onClose, brandId, brandName, initialData }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<AIHealthAnalysisData | null>(null)
  const [isCached, setIsCached] = useState(false)
  const [modelUsed, setModelUsed] = useState("")

  const fetchAnalysis = useCallback(async (refresh = false) => {
    setLoading(true)
    setError(null)
    try {
      const response = await TAgetBrandAIHealthAnalysis(brandId, refresh)
      if (response.success) {
        setData(response.data)
        setIsCached(response.cached || false)
        setModelUsed(response.model || "")
      } else {
        setError(response.error || "Analiz yapılamadı")
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || "Bir hata oluştu")
      } else {
        setError("Bir hata oluştu")
      }
    } finally {
      setLoading(false)
    }
  }, [brandId])

  // Modal açıldığında veri kontrol et
  React.useEffect(() => {
    if (isOpen) {
      setError(null)
      // Eğer initialData varsa onu kullan, API çağrısı yapma
      if (initialData && initialData.labels) {
        setData(initialData)
        setIsCached(true)
        setModelUsed(initialData.model || "")
      } else if (brandId) {
        // initialData yoksa API'den çek
        fetchAnalysis(false)
      } else {
        setData(null)
      }
    }
  }, [isOpen, brandId, initialData, fetchAnalysis])

  const status = data?.labels?.status || "stable"
  const statusInfo = statusConfig[status] || statusConfig.stable
  const healthScore = data?.labels?.health_score ?? 0
  const priorityRank = data?.labels?.priority_rank || "P3"
  const priorityInfo = priorityConfig[priorityRank] || priorityConfig.P3

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-3xl bg-white dark:bg-gray-900 shadow-2xl transition-all">
                {/* Header with Status Color */}
                <div className={`${statusInfo.bg} text-white p-6`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center text-3xl">
                        🤖
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{brandName}</h3>
                        <p className="text-white/80 text-sm">AI Sağlık Analizi</p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Score Display */}
                  {!loading && data && (
                    <div className="mt-6 flex items-center gap-8">
                      {/* Health Score Circle */}
                      <div className="relative w-28 h-28">
                        <svg className="w-28 h-28 transform -rotate-90">
                          <circle
                            cx="56"
                            cy="56"
                            r="48"
                            stroke="rgba(255,255,255,0.2)"
                            strokeWidth="8"
                            fill="none"
                          />
                          <circle
                            cx="56"
                            cy="56"
                            r="48"
                            stroke="white"
                            strokeWidth="8"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={`${(healthScore / 100) * 301.6} 301.6`}
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-bold">{healthScore}</span>
                          <span className="text-xs text-white/70">Health</span>
                        </div>
                      </div>

                      {/* Status & Priority */}
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{statusInfo.emoji}</span>
                          <div>
                            <p className="text-sm text-white/70">Durum</p>
                            <p className="text-xl font-bold">{statusInfo.label}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${priorityInfo.color}`}>
                            {priorityRank}
                          </span>
                          <span className="text-sm text-white/80">{priorityInfo.label}</span>
                        </div>
                      </div>

                      {/* Urgency Score */}
                      {data.labels?.urgency_score !== undefined && (
                        <div className="text-center">
                          <p className="text-4xl font-bold">{data.labels.urgency_score}</p>
                          <p className="text-xs text-white/70">Aciliyet</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                  {loading && (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-primary/20 rounded-full"></div>
                        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin absolute inset-0"></div>
                      </div>
                      <p className="mt-4 text-gray-500 dark:text-gray-400 font-medium">
                        AI analiz yapılıyor...
                      </p>
                    </div>
                  )}

                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center">
                      <span className="text-4xl mb-3 block">❌</span>
                      <p className="font-semibold text-red-700 dark:text-red-300 mb-2">Hata Oluştu</p>
                      <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
                      <button
                        onClick={() => fetchAnalysis(true)}
                        className="px-4 py-2 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/70 transition"
                      >
                        Tekrar Dene
                      </button>
                    </div>
                  )}

                  {data && !loading && (
                    <div className="space-y-6">
                      {/* AI Summary */}
                      {data.ai_summary && (
                        <div className={`${statusInfo.lightBg} ${statusInfo.border} border rounded-2xl p-5`}>
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">💡</span>
                            <div>
                              <h4 className={`font-semibold ${statusInfo.text} mb-1`}>AI Özeti</h4>
                              <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                {data.ai_summary}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Quick Stats Grid */}
                      {data.quick_stats && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <span>📊</span> Hızlı İstatistikler
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {formatNumber(data.quick_stats.days_since_login)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Son Giriş (gün)</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {formatNumber(data.quick_stats.active_campaigns)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Aktif Kampanya</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                                {formatCurrency(data.quick_stats.balance)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Bakiye</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                %{data.quick_stats.balance_usage_percent?.toFixed(0) ?? 0}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Bütçe Kullanım</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
                              <p className={`text-2xl font-bold ${(data.quick_stats.stuck_applications ?? 0) > 0 ? "text-amber-600" : "text-gray-900 dark:text-white"}`}>
                                {formatNumber(data.quick_stats.stuck_applications)}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Takılı Başvuru</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center">
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                %{data.quick_stats.completion_rate?.toFixed(0) ?? 0}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tamamlanma</p>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 text-center col-span-2">
                              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                {formatNumber(data.quick_stats.days_since_last_contact)} gün
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Son İletişim</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Next Action */}
                      {data.next_action && data.next_action.type && data.next_action.type !== "none" && (
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-5">
                          <h4 className="font-semibold text-indigo-700 dark:text-indigo-300 mb-3 flex items-center gap-2">
                            <span>⚡</span> Önerilen Sonraki Aksiyon
                          </h4>
                          <div className="flex items-center gap-4">
                            <span className="px-4 py-2 bg-indigo-500 text-white rounded-xl font-bold text-sm">
                              {actionTypeLabels[data.next_action.type] || data.next_action.type}
                            </span>
                            {data.next_action.priority && (
                              <span className="text-sm text-indigo-600 dark:text-indigo-400">
                                Öncelik: {data.next_action.priority}
                              </span>
                            )}
                          </div>
                          {data.next_action.label && (
                            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                              {data.next_action.label}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Risk Factors */}
                      {data.risk_factors && data.risk_factors.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <span>⚠️</span> Risk Faktörleri
                          </h4>
                          <div className="space-y-2">
                            {data.risk_factors.map((risk, idx) => (
                              <div
                                key={idx}
                                className={`${severityColors[risk.severity]} border rounded-xl p-4 flex items-center justify-between`}
                              >
                                <div className="flex items-center gap-3">
                                  <span className="font-semibold capitalize">{risk.severity}</span>
                                  <span className="text-sm">{risk.label || risk.factor}</span>
                                </div>
                                {risk.value !== undefined && (
                                  <span className="text-sm font-mono">
                                    {risk.value} / {risk.threshold}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Recommendations */}
                      {data.ai_recommendations && data.ai_recommendations.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <span>📋</span> AI Önerileri
                          </h4>
                          <div className="space-y-3">
                            {data.ai_recommendations.map((rec, idx) => (
                              <div
                                key={idx}
                                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4"
                              >
                                <div className="flex items-start gap-3">
                                  {rec.priority && (
                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-bold">
                                      {rec.priority}
                                    </span>
                                  )}
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900 dark:text-white">
                                      {rec.action}
                                    </p>
                                    {rec.expected_impact && (
                                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Beklenen etki: {rec.expected_impact}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Root Causes */}
                      {data.ai_root_causes && data.ai_root_causes.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <span>🔍</span> Kök Nedenler
                          </h4>
                          <ul className="space-y-2">
                            {data.ai_root_causes.map((cause, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <span className="text-red-500 mt-0.5">•</span>
                                {cause}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Tags */}
                      {data.labels?.tags && data.labels.tags.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <span>🏷️</span> Etiketler
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {data.labels.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <div className="flex items-center gap-4">
                            <span>📅 {formatDate(data.analyzed_at)}</span>
                            {modelUsed && <span>🤖 {modelUsed}</span>}
                            {data.processing_time_ms && (
                              <span>⏱️ {(data.processing_time_ms / 1000).toFixed(1)}s</span>
                            )}
                          </div>
                          {isCached && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs">
                              Önbellek
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
                  <div className="text-xs text-gray-400">
                    {data?.labels?.churn_probability !== undefined && (
                      <span>Churn Riski: %{data.labels.churn_probability}</span>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => fetchAnalysis(true)}
                      disabled={loading}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-xl transition disabled:opacity-50 flex items-center gap-2"
                    >
                      <svg className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Yenile
                    </button>
                    <button
                      onClick={onClose}
                      className="px-5 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-xl transition"
                    >
                      Kapat
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default AIHealthAnalysisModal
